import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import JobPostingTable from './components/JobPostingTable';
import JobPostingCard from './components/JobPostingCard';
import JobEditModal from './components/JobEditModal';
import BulkActionsDropdown from './components/BulkActionsDropdown';
import JobFilters from './components/JobFilters';
import Icon from '../../components/AppIcon';

const JobPostingsManagement = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('table');
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    department: '',
    type: ''
  });

  // Mock job postings data
  const [jobPostings, setJobPostings] = useState([
    {
      id: 1,
      title: "Senior Software Engineer",
      department: "Engineering",
      location: "San Francisco, CA",
      type: "Full-time",
      status: "Active",
      applicationCount: 24,
      createdAt: "2024-10-15T10:00:00Z",
      description: `We are looking for a Senior Software Engineer to join our growing engineering team. You will be responsible for designing, developing, and maintaining scalable web applications using modern technologies.\n\nIn this role, you will collaborate with cross-functional teams to deliver high-quality software solutions that meet business requirements and user needs.`,
      requirements: `• 5+ years of experience in software development\n• Proficiency in JavaScript, React, and Node.js\n• Experience with cloud platforms (AWS, GCP, or Azure)\n• Strong understanding of software engineering principles\n• Excellent problem-solving and communication skills`,
      salary: "$120,000 - $160,000",
      experience: "Senior Level"
    },
    {
      id: 2,
      title: "Product Manager",
      department: "Product",
      location: "Remote",
      type: "Full-time",
      status: "Active",
      applicationCount: 18,
      createdAt: "2024-10-14T14:30:00Z",
      description: `Join our product team as a Product Manager where you'll drive the strategy and execution of our core product features. You'll work closely with engineering, design, and business stakeholders to deliver exceptional user experiences.`,
      requirements: `• 3+ years of product management experience\n• Strong analytical and data-driven decision making skills\n• Experience with agile development methodologies\n• Excellent communication and leadership abilities\n• Bachelor's degree in relevant field`,
      salary: "$100,000 - $140,000",
      experience: "Mid Level"
    },
    {
      id: 3,
      title: "UX Designer",
      department: "Design",
      location: "New York, NY",
      type: "Full-time",
      status: "Draft",
      applicationCount: 0,
      createdAt: "2024-10-13T09:15:00Z",
      description: `We're seeking a talented UX Designer to create intuitive and engaging user experiences for our digital products. You'll conduct user research, create wireframes and prototypes, and collaborate with development teams.`,
      requirements: `• 4+ years of UX design experience\n• Proficiency in design tools (Figma, Sketch, Adobe Creative Suite)\n• Strong portfolio demonstrating user-centered design process\n• Experience with user research and usability testing\n• Knowledge of front-end development principles`,
      salary: "$90,000 - $120,000",
      experience: "Mid Level"
    },
    {
      id: 4,
      title: "Marketing Specialist",
      department: "Marketing",
      location: "Austin, TX",
      type: "Full-time",
      status: "Active",
      applicationCount: 31,
      createdAt: "2024-10-12T16:45:00Z",
      description: `Join our marketing team to develop and execute comprehensive marketing campaigns that drive brand awareness and customer acquisition. You'll work across multiple channels including digital, content, and events.`,
      requirements: `• 2+ years of marketing experience\n• Experience with digital marketing tools and platforms\n• Strong writing and content creation skills\n• Knowledge of SEO, SEM, and social media marketing\n• Bachelor's degree in Marketing or related field`,
      salary: "$60,000 - $80,000",
      experience: "Entry Level"
    },
    {
      id: 5,
      title: "Data Scientist",
      department: "Engineering",
      location: "Seattle, WA",
      type: "Full-time",
      status: "Active",
      applicationCount: 15,
      createdAt: "2024-10-11T11:20:00Z",
      description: `We're looking for a Data Scientist to join our analytics team and help drive data-informed decision making across the organization. You'll work with large datasets to uncover insights and build predictive models.`,requirements: `• Master's degree in Data Science, Statistics, or related field\n• 3+ years of experience in data analysis and machine learning\n• Proficiency in Python, R, and SQL\n• Experience with data visualization tools\n• Strong statistical analysis skills`,
      salary: "$110,000 - $150,000",
      experience: "Mid Level"
    },
    {
      id: 6,
      title: "Sales Development Representative",
      department: "Sales",
      location: "Chicago, IL",
      type: "Full-time",
      status: "Closed",
      applicationCount: 42,
      createdAt: "2024-10-10T13:00:00Z",
      description: `Join our sales team as an SDR where you'll be responsible for generating and qualifying leads for our enterprise sales team. This is a great opportunity to start your career in B2B sales.`,
      requirements: `• 1+ years of sales or customer-facing experience\n• Excellent communication and interpersonal skills\n• Strong work ethic and goal-oriented mindset\n• Experience with CRM systems preferred\n• Bachelor's degree preferred`,
      salary: "$50,000 - $70,000",
      experience: "Entry Level"
    },
    {
      id: 7,
      title: "DevOps Engineer",
      department: "Engineering",
      location: "Remote",
      type: "Full-time",
      status: "Active",
      applicationCount: 19,
      createdAt: "2024-10-09T08:30:00Z",
      description: `We're seeking a DevOps Engineer to help build and maintain our cloud infrastructure. You'll work on automation, monitoring, and deployment pipelines to ensure reliable and scalable systems.`,
      requirements: `• 4+ years of DevOps or infrastructure experience\n• Experience with AWS, Docker, and Kubernetes\n• Proficiency in scripting languages (Python, Bash)\n• Knowledge of CI/CD pipelines and automation tools\n• Strong troubleshooting and problem-solving skills`,
      salary: "$100,000 - $130,000",
      experience: "Mid Level"
    },
    {
      id: 8,
      title: "Content Writer",
      department: "Marketing",
      location: "Remote",
      type: "Part-time",
      status: "Active",
      applicationCount: 28,
      createdAt: "2024-10-08T15:45:00Z",
      description: `Join our content team to create engaging blog posts, articles, and marketing materials that educate and inspire our audience. You'll work closely with the marketing team to develop content strategies.`,
      requirements: `• 2+ years of content writing experience\n• Excellent writing and editing skills\n• Experience with SEO best practices\n• Ability to write for different audiences and formats\n• Portfolio of published work`,
      salary: "$30 - $50 per hour",
      experience: "Entry Level"
    }
  ]);

  // Filter and sort jobs
  const filteredAndSortedJobs = React.useMemo(() => {
    let filtered = jobPostings?.filter(job => {
      const matchesSearch = job?.title?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
                           job?.department?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      const matchesStatus = !filters?.status || job?.status === filters?.status;
      const matchesDepartment = !filters?.department || job?.department === filters?.department;
      const matchesType = !filters?.type || job?.type === filters?.type;
      
      return matchesSearch && matchesStatus && matchesDepartment && matchesType;
    });

    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      if (sortConfig?.key === 'createdAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [jobPostings, filters, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSelectJob = (jobId) => {
    setSelectedJobs(prev => 
      prev?.includes(jobId) 
        ? prev?.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedJobs(checked ? filteredAndSortedJobs?.map(job => job?.id) : []);
  };

  const handleCreateJob = () => {
    setEditingJob(null);
    setIsEditModalOpen(true);
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setIsEditModalOpen(true);
  };

  const handleDuplicateJob = (job) => {
    const duplicatedJob = {
      ...job,
      id: Date.now(),
      title: `${job?.title} (Copy)`,
      status: 'Draft',
      applicationCount: 0,
      createdAt: new Date()?.toISOString()
    };
    setJobPostings(prev => [duplicatedJob, ...prev]);
  };

  const handleViewApplications = (job) => {
    navigate('/candidate-pipeline', { state: { jobId: job?.id, jobTitle: job?.title } });
  };

  const handleArchiveJob = (job) => {
    setJobPostings(prev => prev?.filter(j => j?.id !== job?.id));
  };

  const handleSaveJob = (jobData) => {
    if (editingJob) {
      setJobPostings(prev => prev?.map(job => 
        job?.id === editingJob?.id ? { ...job, ...jobData } : job
      ));
    } else {
      const newJob = {
        ...jobData,
        id: Date.now(),
        applicationCount: 0,
        createdAt: new Date()?.toISOString()
      };
      setJobPostings(prev => [newJob, ...prev]);
    }
  };

  const handleBulkAction = (actionId, jobIds) => {
    switch (actionId) {
      case 'activate':
        setJobPostings(prev => prev?.map(job => 
          jobIds?.includes(job?.id) ? { ...job, status: 'Active' } : job
        ));
        break;
      case 'draft':
        setJobPostings(prev => prev?.map(job => 
          jobIds?.includes(job?.id) ? { ...job, status: 'Draft' } : job
        ));
        break;
      case 'close':
        setJobPostings(prev => prev?.map(job => 
          jobIds?.includes(job?.id) ? { ...job, status: 'Closed' } : job
        ));
        break;
      case 'duplicate':
        const jobsToDuplicate = jobPostings?.filter(job => jobIds?.includes(job?.id));
        const duplicatedJobs = jobsToDuplicate?.map(job => ({
          ...job,
          id: Date.now() + Math.random(),
          title: `${job?.title} (Copy)`,
          status: 'Draft',
          applicationCount: 0,
          createdAt: new Date()?.toISOString()
        }));
        setJobPostings(prev => [...duplicatedJobs, ...prev]);
        break;
      case 'archive':
        setJobPostings(prev => prev?.filter(job => !jobIds?.includes(job?.id)));
        break;
      case 'export':
        console.log('Exporting jobs:', jobIds);
        break;
    }
    setSelectedJobs([]);
  };

  return (
    <div className="min-h-screen bg-slate-50/40">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <Breadcrumb />
          
          {/* Page Header with enhanced styling */}
          <div className="flex items-start justify-between mb-10">
            <div className="space-y-1">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 bg-clip-text text-transparent leading-tight">
                Job Postings Management
              </h1>
              <p className="text-slate-600 text-lg font-medium max-w-2xl">
                Create, edit, and manage all job postings across your organization with intelligent insights
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                onClick={() => handleBulkAction('export', filteredAndSortedJobs?.map(job => job?.id))}
                className="border-slate-200 hover:border-slate-300 text-slate-700 hover:text-slate-900 bg-white/80 backdrop-blur-sm shadow-sm hover:shadow-md transition-all duration-200"
              >
                Export All
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={handleCreateJob}
                className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 hover:from-purple-700 hover:via-purple-800 hover:to-indigo-800 text-white shadow-lg hover:shadow-xl border-0 transition-all duration-200 transform hover:scale-105"
              >
                Create New Job
              </Button>
            </div>
          </div>

          {/* Trends Section */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 border border-emerald-200/60 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Icon name="TrendingUp" size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800">Hiring Trends</h3>
                    <p className="text-slate-600">Your recruitment performance this month</p>
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-700">+15%</div>
                    <div className="text-sm text-slate-600">Applications</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-teal-700">+8%</div>
                    <div className="text-sm text-slate-600">Qualified Leads</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-cyan-700">-3d</div>
                    <div className="text-sm text-slate-600">Avg. Fill Time</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced KPI Cards with glass morphism */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
            <div className="group relative bg-white/70 backdrop-blur-md border border-white/60 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-indigo-500/5"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                    <Icon name="Briefcase" size={24} className="text-white" />
                  </div>
                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                </div>
                <div>
                  <p className="text-slate-500 text-sm font-medium mb-1">Total Jobs</p>
                  <p className="text-3xl font-bold text-slate-900">{jobPostings?.length}</p>
                  <p className="text-xs text-purple-600 font-medium mt-2">+2 this week</p>
                </div>
              </div>
            </div>
            
            <div className="group relative bg-white/70 backdrop-blur-md border border-white/60 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-teal-500/5"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                    <Icon name="Play" size={24} className="text-white" />
                  </div>
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                </div>
                <div>
                  <p className="text-slate-500 text-sm font-medium mb-1">Active Jobs</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {jobPostings?.filter(job => job?.status === 'Active')?.length}
                  </p>
                  <p className="text-xs text-emerald-600 font-medium mt-2">87% of total</p>
                </div>
              </div>
            </div>
            
            <div className="group relative bg-white/70 backdrop-blur-md border border-white/60 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-orange-500/5"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                    <Icon name="Edit" size={24} className="text-white" />
                  </div>
                  <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                </div>
                <div>
                  <p className="text-slate-500 text-sm font-medium mb-1">Draft Jobs</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {jobPostings?.filter(job => job?.status === 'Draft')?.length}
                  </p>
                  <p className="text-xs text-amber-600 font-medium mt-2">Ready to publish</p>
                </div>
              </div>
            </div>
            
            <div className="group relative bg-white/70 backdrop-blur-md border border-white/60 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-cyan-500/5"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                    <Icon name="Users" size={24} className="text-white" />
                  </div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                </div>
                <div>
                  <p className="text-slate-500 text-sm font-medium mb-1">Total Applications</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {jobPostings?.reduce((sum, job) => sum + job?.applicationCount, 0)}
                  </p>
                  <p className="text-xs text-blue-600 font-medium mt-2">+15% this month</p>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Filters Section */}
          <div className="bg-white/80 backdrop-blur-md border border-white/60 rounded-2xl shadow-lg mb-8">
            <JobFilters filters={filters} onFilterChange={handleFilterChange} />
          </div>

          {/* Enhanced Action Bar */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-6">
              <div className="bg-white/80 backdrop-blur-md border border-white/60 rounded-xl px-4 py-2 shadow-sm">
                <Checkbox
                  checked={selectedJobs?.length === filteredAndSortedJobs?.length && filteredAndSortedJobs?.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  label={`Select All (${filteredAndSortedJobs?.length})`}
                  className="text-slate-700"
                />
              </div>
              
              <BulkActionsDropdown
                selectedJobs={selectedJobs}
                onBulkAction={handleBulkAction}
              />
            </div>

            <div className="flex items-center gap-2 bg-white/80 backdrop-blur-md border border-white/60 rounded-xl shadow-sm p-1">
              <Button
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                iconName="Table"
                onClick={() => setViewMode('table')}
                className={`h-9 w-9 p-0 transition-all duration-200 ${
                  viewMode === 'table' ?'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg' :'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              />
              <Button
                variant={viewMode === 'card' ? 'default' : 'ghost'}
                size="sm"
                iconName="Grid3X3"
                onClick={() => setViewMode('card')}
                className={`h-9 w-9 p-0 transition-all duration-200 ${
                  viewMode === 'card' ?'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg' :'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
              />
            </div>
          </div>

          {/* Job Listings with enhanced container */}
          <div className="bg-white/60 backdrop-blur-md border border-white/60 rounded-2xl shadow-lg overflow-hidden">
            {viewMode === 'table' ? (
              <JobPostingTable
                jobs={filteredAndSortedJobs}
                sortConfig={sortConfig}
                onSort={handleSort}
                onEdit={handleEditJob}
                onDuplicate={handleDuplicateJob}
                onViewApplications={handleViewApplications}
                onArchive={handleArchiveJob}
              />
            ) : (
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {filteredAndSortedJobs?.map((job) => (
                    <JobPostingCard
                      key={job?.id}
                      job={job}
                      onEdit={handleEditJob}
                      onDuplicate={handleDuplicateJob}
                      onViewApplications={handleViewApplications}
                      onArchive={handleArchiveJob}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Enhanced Empty State */}
          {filteredAndSortedJobs?.length === 0 && (
            <div className="text-center py-16 bg-white/60 backdrop-blur-md border border-white/60 rounded-2xl shadow-lg">
              <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-inner">
                <Icon name="Briefcase" size={40} className="text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">No job postings found</h3>
              <p className="text-slate-600 mb-8 max-w-md mx-auto">
                {Object.values(filters)?.some(filter => filter) 
                  ? "Try adjusting your filters to see more results" :"Get started by creating your first job posting and building your talent pipeline"
                }
              </p>
              {!Object.values(filters)?.some(filter => filter) && (
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleCreateJob}
                  className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 hover:from-purple-700 hover:via-purple-800 hover:to-indigo-800 text-white shadow-lg hover:shadow-xl border-0 transition-all duration-200 transform hover:scale-105"
                >
                  Create New Job
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
      
      {/* Edit Modal */}
      <JobEditModal
        job={editingJob}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveJob}
      />
    </div>
  );
};

export default JobPostingsManagement;